import {useCallback} from "react"
import {useMutation} from "@tanstack/react-query"

import {api} from "@indebted/api"

type AssignOutcome = "assigned" | "conflict" | "forbidden" | "gone" | "rejected" | "failed"

type AssignResult = {
	outcome: AssignOutcome
	reason: string
}

type AssignCustomer = {
	assign: (customerID: string, addSenderEmail: boolean) => Promise<AssignResult>
	assigning: boolean
}

function assignURL(channel: string, conversationID: string, providerMessageID: string): string {
	return api.url(
		`/inbox/unidentified/${encodeURIComponent(channel)}/${encodeURIComponent(conversationID)}` +
			`/messages/${encodeURIComponent(providerMessageID)}/assign`,
	)
}

function outcome(status: number): AssignOutcome {
	switch (status) {
		case 204:
			return "assigned"
		case 400:
			return "rejected"
		case 403:
			return "forbidden"
		case 404:
			return "gone"
		case 409:
			return "conflict"
		default:
			return "failed"
	}
}

// A refusal arrives either as a message or as the field errors the add-email
// command answers with; both say more than "the assignment failed" can.
async function reason(response: Response): Promise<string> {
	const body = await response.json().catch(() => null)
	if (typeof body !== "object" || body === null) return ""

	const {Message: message, Errors: errors} = body as {Message?: unknown; Errors?: unknown}
	if (typeof message === "string" && message !== "") return message

	if (typeof errors === "object" && errors !== null) {
		const stated = Object.entries(errors as Record<string, unknown>)
			.flatMap(([field, messages]) => (Array.isArray(messages) ? messages.map((m) => `${field} ${m}`) : []))
			.filter((entry) => typeof entry === "string")
		if (stated.length > 0) return stated.join(", ")
	}

	return ""
}

function useAssignCustomer(
	channel: string,
	conversationID: string,
	providerMessageID: string,
	agentEmail?: string,
): AssignCustomer {
	// Every refusal is an answer the agent needs to read, so the mutation resolves
	// to an outcome rather than rejecting: nothing here is an exception, and a
	// thrown one would be the same message twice.
	const {mutateAsync, isPending} = useMutation({
		mutationFn: async ({
			customerID,
			addSenderEmail,
		}: {
			customerID: string
			addSenderEmail: boolean
		}): Promise<AssignResult> => {
			if (!agentEmail) return {outcome: "failed", reason: ""}

			try {
				const response = await fetch(assignURL(channel, conversationID, providerMessageID), {
					method: "POST",
					headers: {"Content-Type": "application/json"},
					body: JSON.stringify({
						CustomerID: customerID,
						AgentEmail: agentEmail,
						AddSenderEmail: addSenderEmail,
					}),
				})

				const result = outcome(response.status)
				return {outcome: result, reason: result === "rejected" ? await reason(response) : ""}
			} catch {
				return {outcome: "failed", reason: ""}
			}
		},
	})

	const assign = useCallback(
		(customerID: string, addSenderEmail: boolean) => mutateAsync({customerID, addSenderEmail}),
		[mutateAsync],
	)

	return {assign, assigning: isPending}
}

export {useAssignCustomer}
export type {AssignOutcome}
